import React from "react";
import { motion } from "framer-motion";
import {
  Bell,
  KeyRound,
  Languages,
  Utensils,
  Sparkles,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  Users,
  Wrench,
  BedDouble,
  Activity,
} from "lucide-react";

/**
 * Fully custom-built device mockups (no external images) so they look
 * pixel-perfect at any size — like Stripe / Linear product marketing.
 */

/* ---------------------------- Phone: Guest App ---------------------------- */
export function PhoneGuestMockup({ className = "" }) {
  return (
    <div
      className={`relative w-[240px] md:w-[280px] aspect-[9/19] ${className}`}
      data-testid="mockup-phone-guest"
    >
      {/* Bezel */}
      <div className="absolute inset-0 rounded-[42px] bg-gradient-to-b from-white/10 to-white/[0.03] p-[3px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.05)]">
        <div className="w-full h-full rounded-[38px] bg-[#0b0812] overflow-hidden relative">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-10" />
          {/* Screen */}
          <div className="absolute inset-0 flex flex-col">
            {/* Header */}
            <div className="pt-9 pb-4 px-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono text-white/40 uppercase tracking-widest">
                  Good evening
                </div>
                <div className="text-white font-display text-[18px] leading-tight mt-0.5">
                  Ms. Amelia
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-purple to-brand-orange" />
            </div>
            {/* Digital key card */}
            <div className="mx-4 rounded-2xl p-4 bg-gradient-to-br from-brand-purple to-[#2a1a54] border border-white/10 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-brand-orange/25 blur-2xl" />
              <div className="flex items-center gap-2 text-white/70 text-[10px] font-mono uppercase tracking-widest">
                <KeyRound size={12} /> Digital Key
              </div>
              <div className="font-display text-white text-2xl mt-1">
                Suite 1204
              </div>
              <div className="mt-2 h-1 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full w-2/3 bg-white/70" />
              </div>
              <div className="mt-2 text-[10px] text-white/50 font-mono">
                Valid until Nov 24 · 11:00
              </div>
            </div>
            {/* Quick actions */}
            <div className="mt-4 mx-4 grid grid-cols-3 gap-2">
              {[
                { icon: Utensils, label: "Dining" },
                { icon: Sparkles, label: "Room" },
                { icon: Languages, label: "Concierge" },
              ].map((a, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5 text-center"
                >
                  <a.icon size={14} className="mx-auto text-brand-purpleLight" />
                  <div className="text-[9px] text-white/70 mt-1">{a.label}</div>
                </div>
              ))}
            </div>
            {/* Activity feed */}
            <div className="mt-4 mx-4 rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2 flex-1">
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/40">
                <span>Requests</span>
                <span>Live</span>
              </div>
              {[
                { t: "Room service · Bkfst", s: "ready", icon: CheckCircle2 },
                { t: "Housekeeping", s: "on the way", icon: Clock },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <r.icon
                    size={14}
                    className={
                      r.s === "ready" ? "text-emerald-400" : "text-brand-orange"
                    }
                  />
                  <div className="flex-1">
                    <div className="text-white text-[11px]">{r.t}</div>
                    <div className="text-white/40 text-[9px] font-mono uppercase tracking-widest">
                      {r.s}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Bottom tab */}
            <div className="h-14 mx-4 mt-3 mb-4 rounded-2xl border border-white/10 bg-white/[0.03] flex items-center justify-around">
              {[Sparkles, Utensils, Bell].map((I, i) => (
                <div
                  key={i}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    i === 0
                      ? "bg-brand-orange text-white"
                      : "text-white/50"
                  }`}
                >
                  <I size={14} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Ambient glow */}
      <div className="absolute -inset-6 -z-10 rounded-[64px] bg-brand-purple/20 blur-3xl" />
    </div>
  );
}

/* ---------------------------- Phone: Staff App (mobile) ---------------------------- */
export function PhoneStaffMockup({ className = "" }) {
  const tasks = [
    { r: "1204", t: "Turn-down service", d: "Housekeeping", tag: "high" },
    { r: "807", t: "AC not cooling", d: "Maintenance", tag: "urgent" },
    { r: "1512", t: "Extra towels", d: "Housekeeping", tag: "normal" },
    { r: "603", t: "Late check-out", d: "Front desk", tag: "normal" },
  ];
  return (
    <div
      className={`relative w-[240px] md:w-[280px] aspect-[9/19] ${className}`}
      data-testid="mockup-phone-staff"
    >
      <div className="absolute inset-0 rounded-[42px] bg-gradient-to-b from-white/10 to-white/[0.03] p-[3px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.05)]">
        <div className="w-full h-full rounded-[38px] bg-gradient-to-b from-brand-purple to-brand-purpleDeep overflow-hidden relative">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-10" />
          <div className="absolute inset-0 flex flex-col">
            <div className="pt-9 pb-3 px-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] text-white/60 uppercase tracking-widest font-semibold">
                  Staff · Today
                </div>
                <div className="text-white text-[17px] leading-tight mt-0.5 font-bold">
                  Cynthia E.
                </div>
                <div className="text-[10px] text-white/60">
                  Housekeeping supervisor
                </div>
              </div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-orange to-brand-purpleLight" />
            </div>

            {/* Stat card */}
            <div className="mx-4 rounded-2xl p-3.5 bg-white/10 border border-white/10 relative overflow-hidden">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { l: "Assigned", v: "24" },
                  { l: "SLA", v: "97%" },
                  { l: "Open", v: "05" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-white text-[18px] leading-none font-bold">
                      {s.v}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest text-white/70 mt-1">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tasks list */}
            <div className="mt-3 mx-4 flex-1 space-y-2 overflow-hidden">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-white/70">
                <span className="font-semibold">My tasks</span>
                <span className="font-semibold">Live</span>
              </div>
              {tasks.map((t, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-white/10 border border-white/10 px-3 py-2.5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-md bg-white/15 grid place-items-center text-[10px] text-white font-bold">
                        {t.r}
                      </span>
                      <div className="min-w-0">
                        <div className="text-white text-[11.5px] leading-tight truncate font-semibold">
                          {t.t}
                        </div>
                        <div className="text-white/70 text-[9.5px] uppercase tracking-widest">
                          {t.d}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-bold ${
                        t.tag === "urgent"
                          ? "bg-brand-orange/90 text-white"
                          : t.tag === "high"
                          ? "bg-white text-brand-purple"
                          : "bg-white/15 text-white"
                      }`}
                    >
                      {t.tag}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom tab */}
            <div className="h-14 mx-4 mt-3 mb-4 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-around">
              {[Activity, Users, Wrench, Bell].map((I, i) => (
                <div
                  key={i}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    i === 0
                      ? "bg-brand-orange text-white"
                      : "text-white/70"
                  }`}
                >
                  <I size={14} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -inset-6 -z-10 rounded-[64px] bg-brand-purple/20 blur-3xl" />
    </div>
  );
}

/* ---------------------------- Tablet: Staff ---------------------------- */
export function TabletStaffMockup({ className = "" }) {
  const tasks = [
    { r: "1204", t: "Turn-down service", d: "Housekeeping", u: "Amelia P.", tag: "high" },
    { r: "807", t: "AC not cooling", d: "Maintenance", u: "David K.", tag: "urgent" },
    { r: "1512", t: "Extra towels", d: "Housekeeping", u: "Rio T.", tag: "normal" },
    { r: "603", t: "Late check-out", d: "Front desk", u: "Chen L.", tag: "normal" },
  ];
  return (
    <div
      className={`relative w-[340px] md:w-[440px] aspect-[16/11] ${className}`}
      data-testid="mockup-tablet-staff"
    >
      <div className="absolute inset-0 rounded-[22px] bg-gradient-to-b from-white/10 to-white/[0.03] p-[3px] shadow-[0_50px_100px_-30px_rgba(0,0,0,0.8),0_0_0_1px_rgba(255,255,255,0.05)]">
        <div className="w-full h-full rounded-[19px] bg-[#0b0812] overflow-hidden relative">
          {/* Toolbar */}
          <div className="h-8 border-b border-white/5 px-3 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white/15" />
            <div className="w-2 h-2 rounded-full bg-white/15" />
            <div className="w-2 h-2 rounded-full bg-white/15" />
            <div className="ml-3 text-[10px] font-mono uppercase tracking-widest text-white/40">
              Staff · Assigned to me
            </div>
            <div className="ml-auto flex items-center gap-1.5 text-[10px] text-emerald-400 font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulseGlow" />
              Live
            </div>
          </div>
          {/* Body */}
          <div className="grid grid-cols-[130px_1fr] h-[calc(100%-32px)]">
            <div className="border-r border-white/5 p-3 space-y-1.5">
              {[
                { l: "All tasks", n: 24, active: true, icon: Activity },
                { l: "Housekeeping", n: 12, icon: BedDouble },
                { l: "Maintenance", n: 7, icon: Wrench },
                { l: "Guest reqs", n: 5, icon: Users },
              ].map((it, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[11px] ${
                    it.active
                      ? "bg-brand-purple/25 text-white"
                      : "text-white/60"
                  }`}
                >
                  <it.icon size={12} />
                  <span className="flex-1">{it.l}</span>
                  <span className="font-mono text-[10px] text-white/40">
                    {it.n}
                  </span>
                </div>
              ))}
            </div>
            <div className="p-3 space-y-2 overflow-hidden">
              {tasks.map((t, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2"
                >
                  <div className="w-9 h-9 rounded-md bg-brand-purple/25 grid place-items-center text-brand-purpleLight font-mono text-[11px]">
                    {t.r}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-[12px] truncate">{t.t}</div>
                    <div className="text-white/40 text-[10px] font-mono uppercase tracking-widest truncate">
                      {t.d} · {t.u}
                    </div>
                  </div>
                  <span
                    className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${
                      t.tag === "urgent"
                        ? "bg-brand-orange/20 text-brand-orange"
                        : t.tag === "high"
                        ? "bg-brand-purple/25 text-brand-purpleLight"
                        : "bg-white/5 text-white/50"
                    }`}
                  >
                    {t.tag}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -inset-8 -z-10 rounded-[36px] bg-brand-purple/15 blur-3xl" />
    </div>
  );
}

/* ---------------------------- Laptop: HOP Dashboard ---------------------------- */
export function LaptopDashboardMockup({ className = "" }) {
  return (
    <div
      className={`relative w-[520px] md:w-[720px] aspect-[16/10] ${className}`}
      data-testid="mockup-laptop-hop"
    >
      {/* Screen frame */}
      <div className="absolute inset-0 rounded-[14px] bg-gradient-to-b from-white/10 to-white/[0.03] p-[2px] shadow-[0_60px_120px_-30px_rgba(0,0,0,0.85),0_0_0_1px_rgba(255,255,255,0.05)]">
        <div className="w-full h-full rounded-[12px] bg-[#0b0812] overflow-hidden relative">
          {/* Top bar */}
          <div className="h-7 border-b border-white/5 px-3 flex items-center gap-2">
            <div className="flex gap-1.5">
              <span className="w-2 h-2 rounded-full bg-white/15" />
              <span className="w-2 h-2 rounded-full bg-white/15" />
              <span className="w-2 h-2 rounded-full bg-white/15" />
            </div>
            <div className="ml-4 text-[10px] font-mono uppercase tracking-widest text-white/40">
              HOP · Operations dashboard
            </div>
            <div className="ml-auto flex items-center gap-2 text-[10px] font-mono text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Live · 16 staff
            </div>
          </div>
          {/* Body */}
          <div className="grid grid-cols-[150px_1fr] h-[calc(100%-28px)]">
            {/* Side nav */}
            <div className="border-r border-white/5 p-3 space-y-2">
              <div className="text-[9px] font-mono uppercase tracking-widest text-white/30">
                Overview
              </div>
              {["Today", "Requests", "Housekeeping", "Engineering", "Reports"].map(
                (l, i) => (
                  <div
                    key={i}
                    className={`px-2 py-1.5 rounded-md text-[11px] ${
                      i === 0
                        ? "bg-brand-purple/25 text-white"
                        : "text-white/50"
                    }`}
                  >
                    {l}
                  </div>
                )
              )}
              <div className="mt-4 text-[9px] font-mono uppercase tracking-widest text-white/30">
                Property
              </div>
              <div className="rounded-md border border-white/10 p-2 text-[10px] text-white/70">
                Eclipse Hotel <div className="text-white/40">San Francisco</div>
              </div>
            </div>
            {/* Main */}
            <div className="p-4 space-y-3 overflow-hidden">
              {/* KPI row */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { l: "Task completion", v: "100%", d: "+2.1%" },
                  { l: "SLA compliance", v: "89.8%", d: "+1.4%" },
                  { l: "Requests", v: "2,609", d: "+22%" },
                  { l: "Services", v: "91", d: "types" },
                ].map((k, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5"
                  >
                    <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
                      {k.l}
                    </div>
                    <div className="mt-0.5 flex items-baseline gap-1.5">
                      <div className="text-white font-display text-[18px]">
                        {k.v}
                      </div>
                      <div className="text-[9px] font-mono text-emerald-400">
                        {k.d}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Chart + right panel */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                      Requests · last 30 days
                    </div>
                    <div className="text-[10px] font-mono text-brand-purpleLight">
                      +22% growth
                    </div>
                  </div>
                  <Chart />
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                    Departments
                  </div>
                  {[
                    { l: "Housekeeping", v: 97.9, c: "bg-brand-purpleLight" },
                    { l: "Engineering", v: 81.3, c: "bg-brand-orange" },
                    { l: "Front desk", v: 71.0, c: "bg-white/60" },
                  ].map((r, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-white/70">{r.l}</span>
                        <span className="font-mono text-white/50">
                          {r.v.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-1 rounded-full bg-white/5 overflow-hidden mt-1">
                        <div
                          className={`${r.c} h-full`}
                          style={{ width: `${r.v}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Bottom row */}
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">
                    Live activity
                  </div>
                  <div className="text-[10px] font-mono text-white/40 flex items-center gap-1">
                    <ArrowUpRight size={10} /> real-time
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { r: "1204", t: "Turn-down · assigned", c: "text-brand-purpleLight" },
                    { r: "807", t: "AC repair · SLA 92%", c: "text-brand-orange" },
                    { r: "1512", t: "Towels · done", c: "text-emerald-400" },
                  ].map((a, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-[11px] text-white/70"
                    >
                      <span className={`font-mono text-[10px] ${a.c}`}>
                        #{a.r}
                      </span>
                      <span className="truncate">{a.t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Base */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[110%] h-3 bg-gradient-to-b from-white/10 to-transparent rounded-b-3xl" />
      <div className="absolute -inset-12 -z-10 rounded-[40px] bg-brand-purple/15 blur-3xl" />
    </div>
  );
}

function Chart() {
  const points = [24, 32, 28, 38, 30, 45, 42, 55, 48, 60, 68, 72, 90];
  const max = Math.max(...points);
  const w = 380;
  const h = 100;
  const step = w / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${i * step},${h - (p / max) * h}`)
    .join(" ");
  const area = `${path} L ${w},${h} L 0,${h} Z`;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full h-24 mt-2"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="fillGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#8e75db" stopOpacity="0.5" />
          <stop offset="1" stopColor="#8e75db" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#fillGrad)" />
      <path
        d={path}
        fill="none"
        stroke="#d14f2a"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ---------------------------- Hero Floating Composition ---------------------------- */
export function HeroFloatingMockups() {
  return (
    <div className="relative w-full h-[560px] md:h-[620px]">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-1/2 -translate-x-1/2 top-8"
      >
        <div className="animate-floatSlow">
          <LaptopDashboardMockup />
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: -60, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-[3%] md:left-[6%] bottom-6"
      >
        <div className="animate-float">
          <PhoneGuestMockup />
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 60, y: 20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ duration: 1, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-[2%] md:right-[4%] bottom-16 hidden sm:block"
      >
        <div className="animate-floatSlow">
          <TabletStaffMockup className="w-[300px] md:w-[380px]" />
        </div>
      </motion.div>
    </div>
  );
}
